import { useFormContext, UseFormReturn, useWatch } from "react-hook-form"
import { RestrictiveCovenantsRulesFragment } from "~/gql/graphql"
import {
  getSingleRelevantPaymentRule,
  parseAmountType,
  parsePaymentFrequency,
} from "../helpers/parseRules"
import { RestrictiveCovenantsFormValues } from "../types"
import { useRestrictiveCovenantsContext } from "./useRestrictiveCovenantsContext"

export const getPaymentSectionVisibility = ({
  currentValues,
  knowledgeBase,
}: {
  currentValues?: RestrictiveCovenantsFormValues
  knowledgeBase: RestrictiveCovenantsRulesFragment | undefined
}) => {
  const paymentRule =
    knowledgeBase && currentValues
      ? getSingleRelevantPaymentRule({
          knowledgeBase,
          currentValues,
        })
      : undefined

  const paymentRequired = knowledgeBase?.postTermination?.paymentRequired
  const paymentSectionVisible =
    paymentRequired || paymentRule?.amountRecommended || paymentRule?.amountTopOfMarket

  const amountType = parseAmountType({ currentValues, knowledgeBase })
  const paymentFrequency = parsePaymentFrequency({ currentValues, knowledgeBase })

  // The statutory component is displayed instead of the form...
  const statutoryPaymentVisible =
    /* ...if there's a single `amountType` and frequency amongst all relevant rules for the current clauses and area selections,
    which can also be derived by checking if those fields are disabled
    --> parseAmountType({ ... }).isDisabledField and parsePaymentFrequency({...}).isDisabledField */
    amountType.isDisabledField &&
    paymentFrequency.isDisabledField &&
    // ...and when amountStatutory_* is present and amountRecommended_* or amountTopOfMarket_* are blank.
    paymentRule?.amountStatutory &&
    !paymentRule.amountRecommended &&
    !paymentRule.amountTopOfMarket

  return {
    paymentSectionVisible,
    statutoryPaymentVisible,
  }
}

export const usePaymentSectionVisibility = (
  _form?: UseFormReturn<RestrictiveCovenantsFormValues>,
) => {
  const formFromContext = useFormContext<RestrictiveCovenantsFormValues>()
  const form = _form || formFromContext

  const { knowledgeBase } = useRestrictiveCovenantsContext()

  const currentValues = useWatch({ control: form.control }) as RestrictiveCovenantsFormValues

  return getPaymentSectionVisibility({
    currentValues,
    knowledgeBase,
  })
}
